import type { FC } from 'react';
import React, { useState } from 'react';
import { cssMap } from '@compiled/react';

import { EXTENSION_POINT_PAGE_BANNER_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { useExtensionDimensions } from '@confluence/browser-metrics/entry-points/useExtensionDimensions.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

import { Renderer } from '../core/Renderer.tsx';
import { getMantraNavigationHeight } from '../hooks/getMantraNavigationHeight.ts';
import type { ExtensionPointAppProps } from '../types.ts';
import { ForgeUIExtensionProviderWrapper } from '../main.tsx';
import { FORGE_MODULE_PAGE_BANNER } from '../ForgeModuleType.ts';
import { useForgePageBannerPresetHeight } from '../hooks/useForgePageBannerPresetHeight.ts';

export type PageBannerForgeAppProps = ExtensionPointAppProps & {
	shouldPresetHeight?: boolean;
};

const appWrapperStyles = cssMap({
	default: {
		display: 'block',
	},
	hideApp: {
		height: '0px',
		display: 'none',
	},
});

export const PageBannerForgeApp: FC<PageBannerForgeAppProps> = ({
	shouldPresetHeight = false,
	...props
}) => {
	const { cloudId } = useSessionData();
	const mantraHeight = getMantraNavigationHeight(
		StatsigConfigurations.getString('cc_mantra_navigation_tenant_heights', ''),
		cloudId,
		[props.app],
	);
	const [isAppVisible, setIsAppVisible] = useState(true);
	const renderServerPlaceholder = useIsSSROrHydrating();
	const { presetHeight, onIframeLoad } = useForgePageBannerPresetHeight(shouldPresetHeight);
	const dimensionsRef = useExtensionDimensions(
		FORGE_MODULE_PAGE_BANNER,
		props.app.id,
		props.extensionData.content?.id || props.extensionData.space?.id || '',
	);

	return (
		<div
			ref={dimensionsRef}
			css={[appWrapperStyles['default'], !isAppVisible && appWrapperStyles['hideApp']]}
			data-testid="forge-page-banner-wrapper"
			{...(presetHeight ? { style: { height: `${presetHeight}px` } } : {})}
		>
			{!renderServerPlaceholder && (
				<ForgeUIExtensionProviderWrapper moduleType={FORGE_MODULE_PAGE_BANNER}>
					<ExperienceStart name={EXTENSION_POINT_PAGE_BANNER_EXPERIENCE} />
					<Renderer
						{...props}
						loadingComponent={<div />}
						onIframeLoad={onIframeLoad}
						initialIframeHeight={
							mantraHeight > 0 && expValEquals('cc_mantra_navigation_height', 'isEnabled', true)
								? mantraHeight
								: undefined
						}
						bridge={{
							...props.bridge,
							close: () => {
								setIsAppVisible(false);
							},
							open: () => {
								setIsAppVisible(true);
							},
						}}
					/>
				</ForgeUIExtensionProviderWrapper>
			)}
		</div>
	);
};
